import { useEffect, useMemo, useRef } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton, Typography } from "@mui/material";
import Badge from "@components/Badge/Badge";
import Button from "@components/Button/Button";
import LoadingOverlay from "@components/LoadingOverlay/LoadingOverlay";
import StepperTabPanel from "@components/StepperTabs/StepperTabPanel";
import StepperTabs from "@components/StepperTabs/StepperTabs";
import { STEPPER_TYPE, type StepperTabsHandle } from "@components/StepperTabs/StepperTabs.types";
import CancelPortService from "@features/vessel-call/vessel-call-status/PortServices/flow/CancelPortService";
import { canUserCancelService } from "@features/vessel-call/vessel-call-status/PortServices/flow/hse/utils";
import { isRestrictedLifeboatsMDUser } from "@features/vessel-call/vessel-call-status/PortServices/flow/ship-chandling/utils";
import UpdatePortService from "@features/vessel-call/vessel-call-status/PortServices/flow/UpdatePortService";
import { isBunkering } from "@features/vessel-call/vessel-call-status/PortServices/flow/utils";
import {
  canRejectPortServiceFlowStep,
  getPortServiceFlow,
  isUserRoleExcludedFromFlowStep,
} from "@features/vessel-call/vessel-call-status/PortServices/flow/waste-disposal/utils";
import { getSecondaryVessel } from "@features/vessel-call/vessel-call-status/PortServices/grid/utils";
import withDrawers, {
  type DrawerInjectedProps,
} from "@features/vessel-call/vessel-call-status/PortServices/hoc/withDrawers";
import {
  getPortServiceCategoryLabel,
  getPortServicePreviewData,
  getPortServiceStatusInfo,
  STATUS_KEYS,
} from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import { usePortServicesFormStore } from "@features/vessel-call/vessel-call-status/PortServices/store/usePortServicesForm.store";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { getPortServiceById } from "@services/portServicesApi";
import { useQuery } from "@tanstack/react-query";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

type PortServicePreviewProps = DrawerInjectedProps & {
  shouldRefetch?: boolean;
  setShouldRefetch?: (arg: boolean) => void;
};

const LANG_BASE = "vessel-call.port-services.preview";
const NON_CHANGABLE_STATUSES = [
  STATUS_KEYS.COMPLETED,
  STATUS_KEYS.CLOSED,
  STATUS_KEYS.CANCELLED,
  STATUS_KEYS.REJECTED,
];

const NON_UPDATABLE_STATUSES = [
  STATUS_KEYS.COMPLETED,
  STATUS_KEYS.CLOSED,
  STATUS_KEYS.CANCELLED,
  STATUS_KEYS.REJECTED,
];

const isChangeStatusButtonVisible = (currentStatus: string) =>
  !NON_CHANGABLE_STATUSES.includes(currentStatus);

const isUpdateButtonVisible = (currentStatus: string) =>
  !NON_UPDATABLE_STATUSES.includes(currentStatus);

const PortServicePreview = ({
  openCancelDrawer,
  openChangeStatusDrawer,
  openUploadDocumentDrawer,
  openViewDetailsDrawer,
  openUpdateDrawer,
  shouldRefetch = false,
  setShouldRefetch = () => {},
}: PortServicePreviewProps) => {
  const stepperTabsRef = useRef<StepperTabsHandle>(null);
  const { tabs } = useMemo(() => getPortServicePreviewData(), []);
  const { subModuleId, id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setField, newChatMessage } = usePortServicesFormStore();
  const location = useLocation();

  const handleBack = () => {
    const backPath = location.pathname.replace(/(\/port-services).*/, "$1");
    navigate(backPath);
  };

  const {
    data: portServiceDetails,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["vessel", "bff-vessel-call", subModuleId],
    queryFn: () => getPortServiceById("bff-vessel-call", subModuleId as string),
    enabled: !!subModuleId,
  });

  useEffect(() => {
    if (stepperTabsRef.current?.getActiveStepperTab?.() === undefined) {
      stepperTabsRef.current?.setStepperTab?.(0);
    }
  }, []);

  useEffect(() => {
    if (shouldRefetch) {
      refetch();
      setShouldRefetch(false);
    }
  }, [shouldRefetch, refetch, setShouldRefetch]);

  if (NON_CHANGABLE_STATUSES.includes(portServiceDetails?.status || "") && newChatMessage) {
    setField("newChatMessage", ""); // Clear the chat input if the status is non-changable
  }

  if (isError && !portServiceDetails) {
    return (
      <div className="flex flex-col gap-4" data-testid="port-services-preview-error">
        <Typography variant="h6">{t("common.something-went-wrong")}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t("common.try-again-later")}
        </Typography>
        <div>
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingOverlay loading={isLoading} />;
  }

  const isSecondaryVesselCall =
    getSecondaryVessel(portServiceDetails?.linkedVesselCalls)?.vesselCallId === id;

  const PortServicePreviewHeader = () => {
    const { userType, user, userActionPermissions } = useAuthStore();
    const currentUserType = userType ?? "Other";
    const currentUserRoles = user?.roles ?? [];
    const effectiveCurrentUserType = isRestrictedLifeboatsMDUser({
      category: portServiceDetails?.serviceCategory || "",
      type: portServiceDetails?.serviceType || "",
      currentUserType,
      currentUserRoles,
    })
      ? "Other"
      : currentUserType;
    const userCanCancelPortService = checkPermission(userActionPermissions, "canCancelPortService");

    if (!portServiceDetails) return null;
    const isBunkeringService = isBunkering(portServiceDetails.serviceCategory || "");
    const flow = getPortServiceFlow(
      portServiceDetails.serviceCategory || "",
      portServiceDetails.serviceType || "",
      portServiceDetails.serviceSubType || "",
      portServiceDetails.needsOperationsApproval,
    );
    const currentFlowStep = flow?.[portServiceDetails.status];

    const isCurrentUserRoleExcluded = isUserRoleExcludedFromFlowStep({
      cannotChangeStatusRoles: currentFlowStep?.cannotChangeStatusRoles,
      currentUserRoles,
    });

    const isChangeStatusButtonVisibleForUser = flow
      ? isChangeStatusButtonVisible(portServiceDetails?.status || "") &&
        !!currentFlowStep &&
        !isCurrentUserRoleExcluded &&
        (canRejectPortServiceFlowStep(currentFlowStep, effectiveCurrentUserType) ||
          currentFlowStep.canApprove.includes(effectiveCurrentUserType))
      : isChangeStatusButtonVisible(portServiceDetails?.status || "");

    const isViewDetailsVisible = (portServiceDetails?.lastTaskDecision ?? []).some(
      (decision) =>
        !!decision.rejectionReason ||
        !!decision.rejectionAttachment ||
        !!decision.externalNotes ||
        !!decision.externalAttachment ||
        !!decision.internalNotes ||
        !!decision.internalAttachment ||
        !!decision.comments ||
        !!decision.notes ||
        !!decision.cancellationReason,
    );

    //Shipping Agent can cancel any port service
    const isCancelVisibleShippingAgent = (currentStatus: string, currentUserRoles: string[]) => {
      return (
        currentUserRoles.includes("shipping-agent") &&
        userCanCancelPortService &&
        !NON_CHANGABLE_STATUSES.includes(currentStatus)
      );
    };

    const isCancelButtonVisible =
      canUserCancelService(
        currentUserRoles,
        userCanCancelPortService,
        portServiceDetails.status,
        flow,
      ) || isCancelVisibleShippingAgent(portServiceDetails.status, currentUserRoles);
    const canCancelFromCurrentVesselCall = !isSecondaryVesselCall || isBunkeringService;

    return (
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-start">
            <IconButton
              size="medium"
              sx={{ color: "text.primary", mr: 1 }}
              onClick={handleBack}
              data-testid="port-services-preview-back-button"
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>

            <div>
              <Typography
                variant="h5"
                data-testid="port-services-preview-subtitle"
                sx={{ color: "text.primary" }}
              >
                {getPortServiceCategoryLabel(portServiceDetails.serviceCategory)}
              </Typography>

              <div className="flex flex-wrap items-center gap-3 mt-2 ml-1 text-sm">
                <div className="flex items-center">
                  <Badge
                    label={
                      getPortServiceStatusInfo(
                        portServiceDetails.status,
                        portServiceDetails.serviceCategory,
                      ).label
                    }
                    type={
                      getPortServiceStatusInfo(
                        portServiceDetails.status,
                        portServiceDetails.serviceCategory,
                      ).badgeType
                    }
                  />
                  {isViewDetailsVisible && (
                    <>
                      <span className="text-base ml-2 -mt-1">–</span>
                      <Button
                        testId="port-services-preview-view-details"
                        variant="text"
                        type="button"
                        className="whitespace-nowrap"
                        onClick={() => openViewDetailsDrawer?.(portServiceDetails)}
                        label={
                          <span className="normal-case font-medium">
                            {t("common.view-details")}
                          </span>
                        }
                      />
                    </>
                  )}
                  {isChangeStatusButtonVisibleForUser && !isSecondaryVesselCall && (
                    <>
                      <span className="text-base ml-2 -mt-1">–</span>
                      <Button
                        testId={`port-services-preview-change-status`}
                        variant="text"
                        type="button"
                        className="whitespace-nowrap"
                        onClick={() => openChangeStatusDrawer?.(portServiceDetails)}
                        label={
                          <span className="normal-case font-medium">
                            {t(`${LANG_BASE}.change-status`)}
                          </span>
                        }
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <>
            {isUpdateButtonVisible(portServiceDetails.status) && !isSecondaryVesselCall && (
              <UpdatePortService
                portServiceData={portServiceDetails}
                openUpdateDrawer={openUpdateDrawer}
              />
            )}
            {isCancelButtonVisible && canCancelFromCurrentVesselCall && (
              <CancelPortService
                portServiceData={portServiceDetails}
                openCancelDrawer={openCancelDrawer}
              />
            )}
          </>
        </div>
      </div>
    );
  };

  return (
    <div className="">
      <PortServicePreviewHeader />
      <StepperTabs
        ref={stepperTabsRef}
        testId="port-service-preview-tabs"
        stepperType={STEPPER_TYPE.HORIZONTAL}
        isTypeChangeable={false}
      >
        {portServiceDetails &&
          tabs.map(({ label, component: TabContent, testId }) => {
            return (
              <StepperTabPanel key={label} label={label}>
                <div
                  className="min-w-full flex flex-col bg-white py-8 gap-4 min-w-900px"
                  data-testid={testId}
                >
                  <TabContent
                    langBase={LANG_BASE}
                    portServiceDetails={portServiceDetails}
                    shouldRefetch={shouldRefetch}
                    setShouldRefetch={setShouldRefetch}
                    openUploadDocumentDrawer={openUploadDocumentDrawer}
                  />
                </div>
              </StepperTabPanel>
            );
          })}
      </StepperTabs>
    </div>
  );
};

export default withDrawers(PortServicePreview);
